import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uploadDocumentAction } from "../../api/documentActions";
import BrandLoader from "../../common/components/BrandLoader";
import {
  resolveSlotLaPayload,
  FALLBACK_LA_BY_SLOT,
  COMPANY_ID_CARD_LA,
} from "../../config/documentLaCodes";
import { serializeDocumentFileMetaWithImagePreview } from "../../common/utils/documentFileMeta";
import DocumentFilePreview from "../../common/components/DocumentFilePreview";
import {
  MAX_DOCUMENT_UPLOAD_MB,
  getDocumentUploadOverSizeError,
} from "../../common/utils/documentUploadLimits";
import { useOrganisationListed } from "../../common/hooks/useOrganisationListed";


const SURROGATE_INCOME_PROOF_SLOT = {
  id: "surrogateIncomeProof",
  label: "Surrogate Income Documents",
};

const BASE_DOCUMENT_SLOTS = [
  { id: "photograph", label: "Photograph" },
  { id: "addressProof", label: "Address Proof" },
  { id: "ageProof", label: "Age Proof" },
  { id: "identificationProof", label: "Identification Proof" },
];

const COMPANY_ID_SLOTS = [
  { id: "companyIdFront", label: "Company ID Card (Front)" },
  { id: "companyIdBack", label: "Company ID Card (Back)" },
];

const INCOME_PROOF_SLOT = {
  id: "incomeProof",
  label: "Financial/Income Documents",
};

const FATCA_SLOT = {
  id: "fatcaDeclaration",
  label: "FATCA/CRS Declaration Form",
};

const UploadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
      stroke="#8a1d1d"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#10b981" />
    <path
      d="M8 12l3 3 5-5"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 11v6M14 11v6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const _formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getSlotDisplayLabel = (slotId, slotsList) => {
  const label = slotsList?.find((s) => s.id === slotId)?.label;
  if (label) return label;
  return String(slotId || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
};

const _getSlotFileLabel = (f) => {
  if (!f) return "";
  const isBlobLike =
    typeof Blob !== "undefined" && typeof f === "object" && f instanceof Blob;
  if (typeof f === "object" && f !== null && !isBlobLike) {
    return f.name || f.fileName || "Document";
  }
  return f.name || "Document";
};

const _getSlotFileSizeBytes = (f) => {
  if (!f || typeof f !== "object") return 0;
  return typeof f.size === "number" ? f.size : 0;
};

/* ─── Single Upload Slot ─── */
const UploadSlot = ({ slot, file, error, onFileSelect, onRemove }) => {
  const inputRef = useRef(null);

  const handleClick = () => {
    if (!file) {
      inputRef.current?.click();
    }
  };

  const handleChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      onFileSelect(slot.id, selectedFile);
    }
    // Reset input so same file can be re-selected
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile) {
      onFileSelect(slot.id, droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="upload-slot">
      <label className="upload-slot__label label-one m-0">{slot.label}</label>
      <div
        className={`upload-slot__dropzone ${file ? "has-file" : ""} ${error ? "has-error" : ""}`}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleClick()}
        aria-label={`Upload ${slot.label}`}
      >
        {file ? (
          <div className="upload-slot__file-info">
            <div className="upload-slot__preview">
              <DocumentFilePreview file={file} />
            </div>
            {/* <div className="upload-slot__file-details">
              <div className="upload-slot__file-meta">
                <span className="upload-slot__file-name">
                  {getSlotFileLabel(file)}
                </span>
                <span className="upload-slot__file-size">
                  {formatFileSize(getSlotFileSizeBytes(file))}
                </span>
              </div>
            </div> */}
            <div className="upload-slot__file-actions">
              <CheckIcon />
              <button
                className="upload-slot__remove-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(slot.id);
                }}
                aria-label={`Remove ${slot.label}`}
                type="button"
              >
                <TrashIcon />
              </button>
            </div>
          </div>
        ) : (
          <div className="upload-slot__placeholder">
            <UploadIcon />
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={
            slot.id === "photograph"
              ? ".jpg,.jpeg,.png"
              : ".jpg,.jpeg,.png,.pdf"
          }
          onChange={handleChange}
          className="upload-slot__input"
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
      {error && <span className="upload-slot__error">{error}</span>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   UPLOAD DOCUMENTS MODAL
   ═══════════════════════════════════════════════════ */
const UploadDocumentsModal = ({
  isOpen,
  onClose,
  documentSlots,
  onUploadComplete,
  initialFiles = {},
  applicationNumber: applicationNumberFromProp,
  /** @type {{ formData: object, apiCache: object } | null} */
  documentFormContext = null,
}) => {
  const dispatch = useDispatch();
  const applicationNumberFromStore = useSelector(
    (state) => state.journey?.applicationNumber,
  );
  const leadData = useSelector((state) => state.journey.leadFormData);
  const applicationNumber =
    applicationNumberFromProp || applicationNumberFromStore || "";
  const sumAssured = useSelector((state) => state.journey?.quoteResult?.coverAmount);
  const education = useSelector((state) => state.journey?.leadFormData?.educationalLevel);
  const showSurrogateIncomeDocs = (education === "Graduate" || education === "Post Graduate") && Number(sumAssured) <= 20000000;
  const isOrgListed = useOrganisationListed(
    leadData?.organisation,
    documentFormContext?.apiCache,
  );
  const slots = useMemo(() => {
    let base = [...BASE_DOCUMENT_SLOTS];
    if (isOrgListed) {
      base = [...base, ...COMPANY_ID_SLOTS];
    } else {
      base = [...base, INCOME_PROOF_SLOT];
    }
    if (showSurrogateIncomeDocs) {
      base.push(SURROGATE_INCOME_PROOF_SLOT);
    }
    if (leadData?.nationality === "No") {
      base.push(FATCA_SLOT);
    }
    return base;
  }, [documentSlots, leadData?.nationality, leadData?.organisation, showSurrogateIncomeDocs, isOrgListed]);
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const prevIsOpenRef = useRef(false);

  // Prefill uploaded docs when the modal opens.
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setFiles(initialFiles || {});
      setErrors({});
      setUploadError(null);
      setIsUploading(false);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialFiles]);

  const validateFile = useCallback((file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    const validExtensions = ["jpg", "jpeg", "png", "pdf"];
    if (!validExtensions.includes(ext)) {
      return "Invalid format. Upload jpg, png, or pdf.";
    }
    const sizeError = getDocumentUploadOverSizeError(file);
    if (sizeError) return sizeError;
    return null;
  }, []);

  const handleFileSelect = useCallback(
    async (slotId, file) => {
      const validationError = validateFile(file);
      if (validationError) {
        setErrors((prev) => ({ ...prev, [slotId]: validationError }));
        return;
      }
      if (!applicationNumber) {
        setUploadError("Application number is missing. Please restart the journey.");
        setErrors((prev) => ({ ...prev, [slotId]: "Missing application number." }));
        return;
      }
      try {
        setIsUploading(true);
        setUploadError(null);
        const formData = new FormData();
        formData.append("applicationNumber", applicationNumber);
        // Provide DOB as ownership proof for non-logged-in document upload flows.
        const dateOfBirthForUpload =
          leadData?.dateOfBirth ||
          leadData?.lifeAssuredDob ||
          leadData?.lifeAssuredDob;
        if (dateOfBirthForUpload) {
          formData.append("dateOfBirth", dateOfBirthForUpload);
        }
        formData.append("documentCategory", "KYC");
        const ctx = documentFormContext;
        let la = null;
        if (ctx?.formData != null) {
          la = resolveSlotLaPayload(slotId, {
            formData: ctx.formData,
            apiCache: ctx.apiCache || {},
          });
        }
        if (!la) {
          if (ctx?.formData != null) {
            const slotLabel = getSlotDisplayLabel(slotId, slots);
            const msg = `Please select a valid document proof from ${slotLabel} dropdown in the proposal form.`;
            setUploadError(msg);
            setErrors((prev) => ({
              ...prev,
              [slotId]: msg,
            }));
            setIsUploading(false);
            return;
          }
          la =
            slotId === "idProof"
              ? COMPANY_ID_CARD_LA
              : FALLBACK_LA_BY_SLOT[slotId] || {};
        }
        if (!la.documentTypeCode || !la.documentType) {
          const slotLabel = getSlotDisplayLabel(slotId, slots);
          const msg = `Please select the document type for ${slotLabel}.`;
          setUploadError(msg);
          setErrors((prev) => ({
            ...prev,
            [slotId]: msg,
          }));
          setIsUploading(false);
          return;
        }

        // Persist the LA master `documentTypeCode` in journey formData so BizOps
        // can use it directly (instead of relying on documentID like "70").
        // These keys are consumed by BizOpsPayloadBuilder's resolveLaProofValue().
        if (ctx?.onChange && typeof ctx.onChange === "function") {
          const code = la.documentTypeCode;
          const updates = {};
          switch (slotId) {
            case "addressProof":
              updates.addressProofDocumentTypeCode = code;
              updates.LA_AddressProofDocumentTypeCode = code;
              break;
            case "ageProof":
              updates.ageProofDocumentTypeCode = code;
              updates.LA_AgeProofDocumentTypeCode = code;
              break;
            case "identificationProof":
              updates.identificationProofDocumentTypeCode = code;
              updates.LA_IdentityProofDocumentTypeCode = code;
              break;
            case "idProof":
              updates.identificationProofDocumentTypeCode = code;
              updates.LA_IdentityProofDocumentTypeCode = code;
              break;
            case "incomeProof":
              updates.incomeProofDocumentTypeCode = code;
              break;
            case "surrogateIncomeProof":
              updates.surrogateIncomeProofDocumentTypeCode = code;
              break;
            case "photograph":
              updates.photographDocumentTypeCode = code;
              break;
            case "fatcaDeclaration":
              updates.fatcaDeclarationDocumentTypeCode = code;
              break;
            default:
              break;
          }
          if (Object.keys(updates).length > 0) {
            ctx.onChange("__BULK__", updates);
          }
        }

        formData.append("documentTypeCode", la.documentTypeCode);
        formData.append("documentType", la.documentType);
        formData.append("docFormat", file.name.split(".").pop().toUpperCase());
        formData.append("docPageSideFB", "M");
        formData.append("addedBy", "system");
        formData.append("sourceFrom", "online");
        formData.append("file", file);
        const response = await dispatch(uploadDocumentAction(formData));
        if (!response?.success) {
          throw new Error(response?.errorMessage || "Upload failed");
        }
        const slotMeta = await serializeDocumentFileMetaWithImagePreview(
          file,
          response,
        );
        setFiles((prev) => ({
          ...prev,
          [slotId]: slotMeta,
        }));
        setErrors((prev) => {
          const next = { ...prev };
          delete next[slotId];
          return next;
        });
      } catch (error) {
        setUploadError(
          error?.message || "Document upload failed. Please try again.",
        );
      } finally {
        setIsUploading(false);
      }
    },
    [validateFile, dispatch, applicationNumber, documentFormContext, slots],
  );

  const handleRemove = useCallback((slotId) => {
    setFiles((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
  }, []);

  const handleDone = useCallback(() => {
    if (onUploadComplete) {
      const initial = initialFiles || {};
      // Keys that existed before modal open, but are now removed in the UI.
      const removedSlotIds = Object.keys(initial).filter(
        (slotId) => Boolean(initial[slotId]) && !files[slotId],
      );

      onUploadComplete({
        files,
        removedSlotIds,
      });
    }
    onClose();
  }, [files, onClose, onUploadComplete, initialFiles]);

  const uploadedCount = Object.keys(files).length;

  if (!isOpen) return null;

  return (
    <>
      <div
        className="upload-modal-backdrop"
        onClick={() => !isUploading && handleDone()}
      >
        <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
          {isUploading && (
            <div
              className="quote-premium-loader-overlay"
              aria-live="polite"
              aria-busy="true"
            >
              <BrandLoader color="#97262A" loading={true} size={15} />
            </div>
          )}
          {/* Header */}
          <div className="upload-modal__header">
            <button
              className="upload-modal__back"
              onClick={() => {
                if (isUploading) return;
                setErrors({});
                handleDone();
              }}
              aria-label="Close"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.7081 18.2931C19.801 18.386 19.8747 18.4963 19.9249 18.6177C19.9752 18.7391 20.0011 18.8692 20.0011 19.0006C20.0011 19.132 19.9752 19.2621 19.9249 19.3835C19.8747 19.5048 19.801 19.6151 19.7081 19.7081C19.6151 19.801 19.5048 19.8747 19.3835 19.9249C19.2621 19.9752 19.132 20.0011 19.0006 20.0011C18.8692 20.0011 18.7391 19.9752 18.6177 19.9249C18.4963 19.8747 18.386 19.801 18.2931 19.7081L10.0006 11.4143L1.70806 19.7081C1.52042 19.8957 1.26592 20.0011 1.00056 20.0011C0.735192 20.0011 0.480697 19.8957 0.293056 19.7081C0.105415 19.5204 5.23096e-09 19.2659 0 19.0006C-5.23096e-09 18.7352 0.105415 18.4807 0.293056 18.2931L8.58681 10.0006L0.293056 1.70806C0.105415 1.52042 0 1.26592 0 1.00056C0 0.735192 0.105415 0.480697 0.293056 0.293056C0.480697 0.105415 0.735192 0 1.00056 0C1.26592 0 1.52042 0.105415 1.70806 0.293056L10.0006 8.58681L18.2931 0.293056C18.4807 0.105415 18.7352 -5.23096e-09 19.0006 0C19.2659 5.23096e-09 19.5204 0.105415 19.7081 0.293056C19.8957 0.480697 20.0011 0.735192 20.0011 1.00056C20.0011 1.26592 19.8957 1.52042 19.7081 1.70806L11.4143 10.0006L19.7081 18.2931Z"
                  fill="#3C3D3D"
                ></path>
              </svg>
            </button>
            <h6 className="upload-modal__title">Upload Documents</h6>
            <p className="upload-modal__desc">
              Upload your identity and Address proofs here for quicker
              verification purpose. You can upload jpg, png or pdf file formats
              with a maximum file size limit of {MAX_DOCUMENT_UPLOAD_MB}mb.
            </p>
          </div>

          {/* Upload Grid */}
          <div className="upload-modal__grid">
            {slots.map((slot) => (
              <UploadSlot
                key={slot.id}
                slot={slot}
                file={files[slot.id] || null}
                error={errors[slot.id] || null}
                onFileSelect={handleFileSelect}
                onRemove={handleRemove}
              />
            ))}
          </div>

          {/* Footer */}
          <div className="upload-modal__footer">
            <span className="upload-modal__count">
              {uploadedCount} of {slots.length} documents uploaded
            </span>
            {/* <button
            className="upload-modal__done-btn btn primary-button regular"
            onClick={handleDone}
            disabled={isUploading}
            type="button"
          >
            Done
          </button> */}
          </div>
        </div>

      </div>
      {uploadError && (
        <div className="upload-error-backdrop modal-backdrop">
          <div className="modal-container">
            <div className="modal-header-top">
              {/* <h3 className="modal-title">Upload Failed</h3> */}
            </div>
            <div className="upload-error-modal modal-contents ">
              <p>{uploadError}</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn primary-button regular"
                onClick={() => setUploadError(null)}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadDocumentsModal;
